import { childrenLoader } from "../kinds/registry";
import { KindContext } from "../kinds/types";
import { CatalogStore } from "../model/catalog";
import { ObjectDetailsStore } from "../model/details";
import { httpErrorKind } from "../model/httpError";
import { NodeId, unsavedId } from "../model/node";
import { ObjectServicesStore } from "../model/objectServices";
import { TreeStore } from "../model/tree";

/**
 * Загрузка того, что нужно узлу.
 *
 * Один вход и для выбора, и для раскрытия: оба сначала обеспечивают детали
 * объекта-владельца, а уже потом просят детей. Дедупликацией занимается
 * ресурс, поэтому одновременные выбор и раскрытие дают один запрос.
 */
export class NodeLoading {
  constructor(
    private readonly tree: TreeStore,
    private readonly details: ObjectDetailsStore,
    private readonly services: ObjectServicesStore,
    private readonly catalog: CatalogStore,
    private readonly ctx: KindContext,
  ) {}

  /**
   * Полные данные объекта, которому принадлежит узел.
   * Объект, которого больше нет на сервере, убирается из каталога: продолжать
   * показывать его в дереве нельзя.
   */
  async ensureObject(nodeId: NodeId) {
    const owner = this.tree.ownerObject(nodeId);
    if (!owner || owner.data.id === unsavedId) return;
    try {
      await this.details.ensure(owner.data.id);
    } catch (error) {
      if (httpErrorKind(error) === "notFound") {
        this.forgetObject(owner.data.id);
      }
      throw error;
    }
    await this.services.ensure(owner.data.id);
  }

  /** Дети узла. Вызывается при раскрытии. */
  async loadChildren(nodeId: NodeId) {
    const node = this.tree.node(nodeId);
    if (!node) return;
    await this.ensureObject(nodeId);
    const loader = childrenLoader(node, this.ctx);
    if (!loader) return;
    this.tree.setChildren(nodeId, await loader());
  }

  /**
   * Дети узла, если они ещё не загружены.
   * Нужно перед локальной вставкой: дописывать черновик в незагруженный
   * список нельзя, иначе он подменит собой всех настоящих детей.
   */
  async ensureChildren(nodeId: NodeId) {
    if (this.tree.children(nodeId)) return;
    await this.loadChildren(nodeId);
  }

  /**
   * Перечитывание уже загруженных ветвей после изменения структуры объекта.
   * Незагруженные ветви не трогаются: они и так подтянутся при раскрытии.
   */
  async reloadLoaded(nodeId: NodeId) {
    if (!this.tree.children(nodeId)) return;
    await this.loadChildren(nodeId);
    const children = this.tree.children(nodeId) ?? [];
    await Promise.all(children.map((id) => this.reloadLoaded(id)));
  }

  /**
   * Каталог только что перечитан. Детали могли измениться у другого
   * пользователя, пока вкладка была закрыта.
   *
   * Кэш забывается; уже раскрытые ветви пересобираются. Нераскрытые
   * подтянутся при раскрытии — для них отдельного запроса нет.
   */
  async refreshAfterCatalogReload() {
    this.details.clear();
    this.services.clear();
    await Promise.allSettled(
      this.tree.rootIds.map((id) => this.reloadLoaded(id)),
    );
  }

  forgetObject(objectId: number | string) {
    this.details.forget(objectId);
    this.services.forget(objectId);
    this.catalog.remove(objectId);
  }
}
